import axios, { CancelTokenSource } from 'axios';

import {
  ContactFlow,
  ContactFlowData,
  GetContactFlowDataEncoder,
  GetContactFlowDecoder,
  GetContactFlowsResponseDecoder,
  GetPredictiveAgentStatusesDecoder,
  GetPredictiveIvrMessagesResponseDecoder,
  GetSipQueuesResponseDecoder,
  GetTrunkFlowsResponseDecoder,
  NewSipQueue,
  PredictiveAgentStatus,
  PredictiveIvrMessage,
  SipQueue,
  SipQueueResponse as SipQueuesResponse,
  TrunkFlow,
  UpsertTrunkFlow,
} from '~pages/SipConfiguration/domain';
import { APIError, UnsupportedStructureError } from '~services/Errors';

export const getPredictiveIvrMessages = async (): Promise<PredictiveIvrMessage[]> => {
  const path = '/api/predictive-ivr-messages';
  let resp;

  try {
    resp = await axios.request({
      method: 'GET',
      url: path,
      withCredentials: true,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = GetPredictiveIvrMessagesResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const upsertPredictiveIvrMessage = async (isUpdate: boolean, msg: PredictiveIvrMessage): Promise<void> => {
  const method = isUpdate ? 'PUT' : 'POST';
  const path = isUpdate ? `/api/predictive-ivr-messages/${msg.key}` : `/api/predictive-ivr-messages/`;

  let body: any = msg;
  if (msg.file) {
    body = new FormData();
    body.append('file', msg.file);
    body.append('message', JSON.stringify({ ...msg, file: undefined }));
  }

  try {
    await axios.request({
      method: method,
      url: path,
      withCredentials: true,
      data: body,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export const deletePredictiveIvrMessage = async (key: string): Promise<void> => {
  const path = `/api/predictive-ivr-messages/${key}`;

  try {
    await axios.request({
      method: 'DELETE',
      url: path,
      withCredentials: true,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export const getS3AudioFileFromUrl = async (url: string): Promise<Blob> => {
  try {
    const response = await axios.request({
      method: 'GET',
      url: url,
      responseType: 'blob',
      headers: {
        'Cache-control': 'public, max-age=900',
      },
    });

    return response.data;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }
};

export const getPredictiveAgentStatuses = async (): Promise<PredictiveAgentStatus[]> => {
  const path = `/api/predictive-agent-statuses`;
  let resp;
  try {
    resp = await axios.request({
      method: 'GET',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = GetPredictiveAgentStatusesDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const postPredictiveAgentStatus = async (status: PredictiveAgentStatus): Promise<void> => {
  const path = `/api/predictive-agent-statuses`;
  const data = {
    agent_status: status.agentStatus,
    description: status.description,
    position: status.position,
  };

  try {
    await axios.request({
      data,
      method: 'POST',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export const putPredictiveAgentStatuses = async (statuses: PredictiveAgentStatus[]): Promise<void> => {
  const path = `/api/predictive-agent-statuses`;
  const body = {
    statuses: statuses.map((status) => ({
      agent_status: status.agentStatus,
      position: status.position,
      description: status.description,
    })),
  };
  try {
    await axios.request({
      method: 'PUT',
      url: path,
      headers: {
        Accept: 'application/json',
      },
      data: body,
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export const deletePredictiveAgentStatus = async (status: string): Promise<void> => {
  const path = `/api/predictive-agent-statuses/${status}`;
  try {
    await axios.request({
      method: 'DELETE',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export const getSipQueuesByNextUrl = async (
  nextUrl: string,
  cancelToken?: CancelTokenSource,
): Promise<SipQueuesResponse | undefined> => {
  const path = nextUrl;
  let resp;

  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken ? cancelToken.token : undefined,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = GetSipQueuesResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const getSipQueues = async ({
  search,
  queueIds,
  cancelToken,
}: {
  search?: string;
  queueIds?: number[];
  cancelToken?: CancelTokenSource;
}): Promise<SipQueuesResponse | undefined> => {
  const path = `/api/internal/predictive-contact-flows/queues/`;
  let resp;
  try {
    resp = await axios.request({
      method: 'GET',
      url: path,
      params: {
        search: search?.toLowerCase() || undefined,
        queue_ids: queueIds,
      },
      paramsSerializer: {
        indexes: null,
      },
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken?.token,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }

  const decoded = GetSipQueuesResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const createSipQueue = async (queue: NewSipQueue): Promise<void> => {
  const path = `/api/predictive-contact-flows/queues/`;
  const data = {
    queue_name: queue.queueName,
    description: queue.description,
  };
  try {
    await axios.request({
      data,
      method: 'POST',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }
};

export const updateSipQueue = async (queue: SipQueue): Promise<void> => {
  const path = `/api/predictive-contact-flows/queues/${queue.queueID}/`;
  const data = {
    queue_name: queue.queueName,
    description: queue.description,
  };
  try {
    await axios.request({
      data,
      method: 'PUT',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }
};

export const deleteSipQueue = async (queueID: number): Promise<void> => {
  const path = `/api/predictive-contact-flows/queues/${queueID}/`;
  try {
    await axios.request({
      method: 'DELETE',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }
};

export const getTrunkFlows = async (): Promise<TrunkFlow[]> => {
  const path = `/api/internal/predictive-contact-flows/trunk-flows/`;
  let resp;
  try {
    resp = await axios.request({
      method: 'GET',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }

  const decoded = GetTrunkFlowsResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const createTrunkFlow = async (trunk: UpsertTrunkFlow): Promise<void> => {
  const path = `/api/predictive-contact-flows/trunk-flows/`;
  const data = {
    trunk: trunk.trunk,
    contact_flow_id: trunk.contactFlowId,
  };
  try {
    await axios.request({
      data,
      method: 'POST',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }
};

export const updateTrunkFlow = async (trunk: UpsertTrunkFlow): Promise<void> => {
  const path = `/api/predictive-contact-flows/trunk-flows/${encodeURIComponent(trunk.trunk)}/`;
  const data = {
    trunk: trunk.trunk,
    contact_flow_id: trunk.contactFlowId,
  };
  try {
    await axios.request({
      data,
      method: 'PUT',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }
};

export const deleteTrunkFlow = async (trunk: string): Promise<void> => {
  const path = `/api/predictive-contact-flows/trunk-flows/${encodeURIComponent(trunk)}/`;
  try {
    await axios.request({
      method: 'DELETE',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }
};

export const getContactFlows = async (): Promise<ContactFlow[]> => {
  const path = '/api/predictive-contact-flows/flows/';
  let resp;
  try {
    resp = await axios.request({
      method: 'GET',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }

  const decoded = GetContactFlowsResponseDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const getContactFlow = async (id: number): Promise<ContactFlow> => {
  const path = `/api/predictive-contact-flows/flows/${id}`;
  let resp;
  try {
    resp = await axios.request({
      method: 'GET',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }

  const decoded = GetContactFlowDecoder.run(resp.data);

  if (decoded.ok === false) {
    const err = new UnsupportedStructureError(decoded.error.message);

    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const createContactFlow = async (data: ContactFlowData): Promise<void> => {
  const path = `/api/predictive-contact-flows/flows/`;
  try {
    const cf = GetContactFlowDataEncoder.run(data);
    if (cf.ok) {
      await axios.request({
        data: cf.result,
        method: 'POST',
        url: path,
        headers: {
          Accept: 'application/json',
        },
      });
    } else {
      throw new Error(cf.error.message);
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const res = e.response!.data;
      if (res) {
        console.error(res);
      }
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }
};

export const updateContactFlow = async (contactFlowId: number, data: ContactFlowData): Promise<void> => {
  const path = `/api/predictive-contact-flows/flows/${encodeURIComponent(contactFlowId)}/`;
  try {
    const cf = GetContactFlowDataEncoder.run(data);
    if (cf.ok) {
      await axios.request({
        data: {
          contact_flow_id: contactFlowId,
          ...cf.result,
        },
        method: 'PUT',
        url: path,
        headers: {
          Accept: 'application/json',
        },
      });
    } else {
      throw new Error(cf.error.message);
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const res = e.response!.data;
      if (res) {
        console.error(res);
      }
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }
};

export const deleteContactFlow = async (id: number): Promise<void> => {
  const path = `/api/predictive-contact-flows/flows/${encodeURIComponent(id)}/`;
  try {
    await axios.request({
      method: 'DELETE',
      url: path,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, e.message);
    }
    throw new APIError(-1, e as string);
  }
};
